import { useEffect, useMemo, useState } from "react";
import { Search, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import InventorySearchDialog from "./InventorySearchDialog";

const MAX_LINES = 20;
const KIND_LABEL = { invoice: "Factura", delivery_note: "Albarán", credit_note: "Abono" };

function emptyDocLine() {
  return {
    _uid: `docline-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    product_id: null,
    sku: "",
    name: "",
    description: "",
    kind: "product",
    repair_id_ref: "",
    quantity: 1,
    unit_price: 0,
    vat_rate: 21,
  };
}

function Dialog({ children, onClose, title, wide }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className={`bg-white border border-[#111110] shadow-hard w-full ${wide ? "max-w-2xl" : "max-w-sm"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E0]">
          <div className="font-display font-bold text-sm">{title}</div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F7F4]">
            <X size={14} />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

/**
 * Diálogo de creación de Factura / Albarán.
 * Extraído de Billing.jsx (iteration_28).
 *
 * Props:
 *  - onClose: cerrar el diálogo
 *  - onCreated: callback con el documento recién creado
 */
export default function NewDocDialog({ onClose, onCreated }) {
  const [kind, setKind] = useState("invoice");
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [custQuery, setCustQuery] = useState("");
  const [parts, setParts] = useState([]);
  const [items, setItems] = useState([emptyDocLine()]);
  const [paymentMethod, setPaymentMethod] = useState("Pendiente");
  const [openSearch, setOpenSearch] = useState(null);
  const [showInvSearch, setShowInvSearch] = useState(false);
  const [partQuery, setPartQuery] = useState("");

  useEffect(() => {
    api.get("/customers")
      .then((r) => setCustomers(r.data))
      .catch((err) => console.warn("[NewDocDialog] /customers falló:", err));
    api.get("/parts")
      .then((r) => setParts(r.data))
      .catch((err) => console.warn("[NewDocDialog] /parts falló:", err));
  }, []);

  const filteredCustomers = useMemo(() => {
    if (!custQuery.trim()) return customers.slice(0, 8);
    const n = custQuery.toLowerCase();
    return customers.filter((c) =>
      (c.name || "").toLowerCase().includes(n) ||
      (c.code || "").toLowerCase().includes(n) ||
      (c.tax_id || "").toLowerCase().includes(n) ||
      (c.billing_name || "").toLowerCase().includes(n) ||
      (c.billing_tax_id || "").toLowerCase().includes(n) ||
      (c.phone || "").toLowerCase().includes(n) ||
      (c.email || "").toLowerCase().includes(n)
    ).slice(0, 10);
  }, [custQuery, customers]);

  const filteredParts = useMemo(() => {
    if (!partQuery.trim()) return parts.slice(0, 8);
    const n = partQuery.toLowerCase();
    return parts.filter((p) =>
      (p.name || "").toLowerCase().includes(n) ||
      (p.sku || "").toLowerCase().includes(n) ||
      (p.id || "").toLowerCase().includes(n) ||
      (p.category || "").toLowerCase().includes(n) ||
      (p.description || "").toLowerCase().includes(n)
    ).slice(0, 10);
  }, [partQuery, parts]);

  const setItem = (i, patch) => {
    const next = items.slice();
    next[i] = { ...next[i], ...patch };
    setItems(next);
  };
  const addLine = () => items.length < MAX_LINES && setItems([...items, emptyDocLine()]);
  const removeLine = (i) =>
    setItems(items.length > 1 ? items.filter((_, idx) => idx !== i) : [emptyDocLine()]);

  const pickPart = (i, p) => {
    setItem(i, {
      product_id: p.id,
      sku: p.sku,
      name: p.name,
      description: items[i]?.description || p.description || "",
      kind: p.kind || "product",
      unit_price: p.sale_price || 0,
      vat_rate: p.vat_rate ?? 21,
      // Iter 117 — Heredamos la garantía del inventario exactamente.
      warranty_months: Number(p.warranty_months) || 0,
    });
    setOpenSearch(null);
    setPartQuery("");
  };

  const submit = async () => {
    if (!customer) {
      toast.error("Selecciona un cliente");
      return;
    }
    const cleaned = items.filter((it) => it.name);
    if (!cleaned.length) {
      toast.error("Añade al menos una línea con concepto");
      return;
    }
    try {
      const r = await api.post("/documents", {
        kind,
        customer_id: customer.id,
        items: cleaned,
        payment_method: paymentMethod,
      });
      toast.success(`${KIND_LABEL[kind]} ${r.data.code} creado`);
      onCreated(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Dialog onClose={onClose} title="Nuevo documento" wide>
      <div className="space-y-4">
        {/* Kind + payment */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="font-mono text-[10px] uppercase text-[#525252]">Tipo</label>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              data-testid="newdoc-kind"
              className="mt-1 w-full ds-card outline-none px-3 py-2 text-sm rounded-none"
            >
              <option value="invoice">Factura</option>
              <option value="delivery_note">Albarán</option>
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase text-[#525252]">Forma de pago</label>
            <input
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="mt-1 w-full ds-card outline-none px-3 py-2 text-sm rounded-none"
            />
          </div>
        </div>

        {/* Customer search */}
        <div>
          <label className="font-mono text-[10px] uppercase text-[#525252]">Cliente *</label>
          {customer ? (
            <div className="mt-1 border border-[#6E8B3D] bg-[#F0F4E8] p-3 flex items-start justify-between">
              <div className="text-sm">
                <div className="font-mono text-[10px] text-[#525252]">{customer.code}</div>
                <div className="font-semibold">
                  {customer.is_company && customer.billing_name ? customer.billing_name : customer.name}
                </div>
                <div className="text-xs font-mono text-[#525252]">
                  {customer.is_company ? customer.billing_tax_id : customer.tax_id}
                </div>
                <div className="text-xs text-[#525252]">
                  {customer.is_company ? customer.billing_address : customer.address}
                </div>
                <div className="text-xs text-[#525252]">
                  {customer.is_company
                    ? `${customer.billing_postal_code || ""} ${customer.billing_city || ""}`
                    : `${customer.postal_code || ""} ${customer.city || ""}`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setCustomer(null); setCustQuery(""); }}
                className="text-[10px] font-mono uppercase text-[#525252] hover:text-red-700"
                data-testid="newdoc-customer-change"
              >
                Cambiar
              </button>
            </div>
          ) : (
            <>
              <div className="relative mt-1">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#525252]" />
                <input
                  value={custQuery}
                  onChange={(e) => setCustQuery(e.target.value)}
                  placeholder="Buscar por nombre, código, NIF, teléfono, email..."
                  data-testid="newdoc-customer-search"
                  className="w-full ds-card focus:border-[#6E8B3D] outline-none pl-8 pr-3 py-2 text-sm rounded-none"
                />
              </div>
              <div className="mt-1 border border-[#E5E5E0] max-h-40 overflow-y-auto">
                {filteredCustomers.length === 0 ? (
                  <div className="p-3 text-xs text-[#525252] text-center">Sin resultados</div>
                ) : filteredCustomers.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCustomer(c)}
                    data-testid={`newdoc-customer-pick-${c.id}`}
                    className="w-full text-left px-3 py-2 border-b border-[#E5E5E0] last:border-0 hover:bg-[#F0F4E8]"
                  >
                    <div className="text-sm font-semibold">{c.name}</div>
                    <div className="text-[10px] font-mono text-[#525252]">
                      {c.code} {c.tax_id && `· ${c.tax_id}`} {c.phone && `· ${c.phone}`}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Lines */}
        <div>
          <label className="font-mono text-[10px] uppercase text-[#525252]">
            Líneas (máx {MAX_LINES})
          </label>
          <div className="mt-1 space-y-2">
            {items.map((it, i) => (
              <div
                key={it._uid || `newdoc-${i}`}
                className="border border-[#E5E5E0] p-2 bg-[#F7F7F4]"
                data-testid={`newdoc-line-${i}`}
              >
                {/* Row 1 */}
                <div className="grid grid-cols-12 gap-1 text-xs">
                  <div className="col-span-3">
                    <div className="font-mono text-[9px] uppercase text-[#525252]">ID Reparación</div>
                    <input
                      value={it.repair_id_ref || ""}
                      onChange={(e) => setItem(i, { repair_id_ref: e.target.value })}
                      placeholder="REP-2026-0001"
                      data-testid={`newdoc-line-repair-${i}`}
                      className="w-full ds-card outline-none px-2 py-1 font-mono rounded-none"
                    />
                  </div>
                  <div className="col-span-5 relative">
                    <div className="font-mono text-[9px] uppercase text-[#525252]">Concepto</div>
                    <div className="flex items-center gap-1">
                      <input
                        value={it.name || ""}
                        onChange={(e) => setItem(i, { name: e.target.value })}
                        placeholder="Editable o búscalo en inventario"
                        data-testid={`newdoc-line-name-${i}`}
                        className="flex-1 ds-card outline-none px-2 py-1 rounded-none"
                      />
                      <button
                        type="button"
                        onClick={() => setOpenSearch(openSearch === i ? null : i)}
                        data-testid={`newdoc-line-concept-${i}`}
                        title="Buscar en inventario"
                        className="border border-[#E5E5E0] hover:border-[#111110] bg-white px-1.5 py-1"
                      >
                        <Search size={12} />
                      </button>
                    </div>
                    {it.sku && <div className="font-mono text-[9px] text-[#525252] mt-0.5">{it.sku}</div>}
                    {openSearch === i && (
                      <div className="absolute z-10 left-0 right-0 mt-1 border border-[#6E8B3D] bg-white shadow-hard-sm">
                        <input
                          autoFocus
                          value={partQuery}
                          onChange={(e) => setPartQuery(e.target.value)}
                          placeholder="Buscar por código, nombre, descripción..."
                          data-testid={`newdoc-line-search-${i}`}
                          className="w-full border-b border-[#E5E5E0] outline-none px-2 py-1.5 text-xs"
                        />
                        <div className="max-h-44 overflow-y-auto">
                          {filteredParts.length === 0 ? (
                            <div className="p-2 text-[10px] text-[#525252]">Sin resultados. Crea en Inventario.</div>
                          ) : filteredParts.map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => pickPart(i, p)}
                              data-testid={`newdoc-line-pick-${i}-${p.id}`}
                              className="w-full text-left px-2 py-1 hover:bg-[#F0F4E8] border-b border-[#E5E5E0] last:border-0"
                            >
                              <div className="font-semibold text-xs">{p.name}</div>
                              <div className="font-mono text-[10px] text-[#525252]">
                                {p.sku} · {p.kind === "service" ? "Servicio" : "Producto"} · €{p.sale_price?.toFixed(2)}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="col-span-3">
                    <div className="font-mono text-[9px] uppercase text-[#525252]">Cant · Precio · IVA%</div>
                    <div className="grid grid-cols-3 gap-1">
                      <input
                        type="number"
                        value={it.quantity}
                        step="0.01"
                        onChange={(e) => setItem(i, { quantity: Number(e.target.value) || 0 })}
                        title="Cantidad"
                        className="ds-card outline-none px-1 py-1 font-mono text-right rounded-none"
                      />
                      <input
                        type="number"
                        value={it.unit_price}
                        step="0.01"
                        onChange={(e) => setItem(i, { unit_price: Number(e.target.value) || 0 })}
                        title="Precio unitario"
                        className="ds-card outline-none px-1 py-1 font-mono text-right rounded-none"
                      />
                      <input
                        type="number"
                        value={it.vat_rate}
                        step="0.01"
                        onChange={(e) => setItem(i, { vat_rate: Number(e.target.value) || 0 })}
                        title="IVA %"
                        className="ds-card outline-none px-1 py-1 font-mono text-right rounded-none"
                      />
                    </div>
                  </div>
                  <div className="col-span-1 flex items-end justify-end">
                    <button
                      type="button"
                      onClick={() => removeLine(i)}
                      className="p-1 text-[#525252] hover:text-red-700"
                      data-testid={`newdoc-line-remove-${i}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                </div>
                {/* Row 2 */}
                <div className="mt-1.5">
                  <div className="font-mono text-[9px] uppercase text-[#525252]">Observaciones</div>
                  <input
                    value={it.description || ""}
                    onChange={(e) => setItem(i, { description: e.target.value })}
                    placeholder="Texto libre para esta línea (aparece en el PDF)"
                    data-testid={`newdoc-line-obs-${i}`}
                    className="w-full ds-card outline-none px-2 py-1 rounded-none"
                  />
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addLine}
            disabled={items.length >= MAX_LINES}
            data-testid="newdoc-line-add"
            className="mt-2 text-xs text-[#6E8B3D] hover:underline inline-flex items-center gap-1 disabled:opacity-50"
          >
            <Plus size={11} /> Añadir línea ({items.length}/{MAX_LINES})
          </button>
          {/* Iter 117 — Lupa: modal para añadir múltiples artículos del inventario. */}
          <button
            type="button"
            onClick={() => setShowInvSearch(true)}
            disabled={items.length >= MAX_LINES}
            data-testid="newdoc-open-inventory-search"
            className="mt-2 ml-3 text-xs text-[#111110] hover:underline inline-flex items-center gap-1 disabled:opacity-50"
            title="Buscar y añadir varios artículos del inventario"
          >
            <Search size={11} /> Añadir del inventario
          </button>
        </div>

        <button
          onClick={submit}
          disabled={!customer || !items.some((it) => it.name)}
          data-testid="newdoc-submit"
          className="w-full bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-2.5 text-sm font-semibold disabled:opacity-50"
        >
          Crear documento
        </button>
      </div>
      <InventorySearchDialog
        open={showInvSearch}
        onClose={() => setShowInvSearch(false)}
        parts={parts}
        onAdd={(picked) => {
          // Iter 117 — Añadimos los seleccionados como nuevas líneas.
          const newLines = picked.map((p) => ({
            ...emptyDocLine(),
            product_id: p.product_id,
            sku: p.sku,
            name: p.name,
            description: p.description || "",
            kind: p.kind,
            quantity: p.quantity,
            unit_price: p.unit_price,
            vat_rate: p.vat_rate,
            warranty_months: p.warranty_months,
          }));
          // Si solo hay una línea vacía inicial, la reemplazamos.
          const clean = items.filter((it) => (it.name || "").trim() !== "" || (it.product_id));
          const merged = clean.length === 0 ? newLines : [...clean, ...newLines];
          setItems(merged.slice(0, MAX_LINES));
        }}
      />
    </Dialog>
  );
}
